import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { getIn, useFormikContext } from 'formik'
import { ENTITY_TYPES } from '@sales-center/common'
import { useDebounce } from '@user/utils/useDebounce'
import { useGetSearchResults } from '@user/features/GlobalSearch/Search/hooks/useGetSearchResults'
import type { SearchListItem } from '@user/store/search/typings'

const MIN_CHARS = 3

// The /search response entityType is a display string; the activity payload and
// entity-dependent form fields use the ENTITY_TYPES enum. DraftLead is excluded
// (E3) — attach is Lead + Opportunity only.
function mapEntityType(raw: string): ENTITY_TYPES | null {
  if (raw === 'Lead') return ENTITY_TYPES.Lead
  if (raw === 'Opportunity') return ENTITY_TYPES.Opportunity
  return null
}

function optionLabel(item: SearchListItem) {
  const secondary = item.name || item.phone || item.email || ''
  if (item.companyName) {
    return secondary ? `${item.companyName} — ${secondary}` : item.companyName
  }
  // Never return '' — MUI warns and renders a blank, unselectable-looking row.
  return secondary || `${item.entityType} #${item.entityId}`
}

// SH-1432: the Formik model carries the ENTITY_TYPES value ('LEAD'); the picker's
// SearchListItem uses the display string ('Lead'). Map back so the pre-filled
// edit-mode chip/label reads naturally.
function entityTypeDisplay(type: ENTITY_TYPES): string {
  if (type === ENTITY_TYPES.Opportunity) return 'Opportunity'
  if (type === ENTITY_TYPES.DraftLead) return 'DraftLead'
  return 'Lead'
}

export type AttachedRecord = {
  salesTeamId: number | null
  entityType: ENTITY_TYPES
}

type Props = {
  // SH-1432: edit mode renders Attach-To pre-filled and non-editable (REQ-3). The
  // entity is read from the Formik model (seeded by useInitValues). Defaults false
  // so the create flow is unchanged.
  disabled?: boolean
  onRecordChange?: (record: AttachedRecord | null) => void
}

export function AttachToPicker({ disabled = false, onRecordChange }: Props) {
  const { values, errors, touched, setValues, setFieldTouched } =
    useFormikContext<{
      entityId?: number | null
      entityType?: ENTITY_TYPES | null
    }>()

  const [input, setInput] = useState('')
  // Seed the selection from the Formik model so edit mode shows the already
  // attached entity. In create mode entityId/entityType are unset, so this is null
  // and behaviour is unchanged.
  const [selectedItem, setSelectedItem] = useState<SearchListItem | null>(() =>
    values.entityId != null && values.entityType != null
      ? ({
          entityId: values.entityId,
          entityType: entityTypeDisplay(values.entityType),
        } as SearchListItem)
      : null,
  )

  const formEntityId = values.entityId ?? null
  useEffect(() => {
    if (formEntityId != null || !selectedItem) return
    setSelectedItem(null)
    onRecordChange?.(null)
  }, [formEntityId, selectedItem, onRecordChange])
  const debounced = useDebounce(input, 500)
  const { searchStatus, searchData } = useGetSearchResults(debounced)

  // Server-side search already scopes by permission (P4); only narrow to
  // Lead/Opportunity here (E3 — exclude DraftLead). Open and closed both show.
  const options = useMemo(
    () =>
      (searchData?.searchList ?? []).filter(
        (item) =>
          item.entityType === 'Lead' || item.entityType === 'Opportunity',
      ),
    [searchData],
  )

  // Keep the current selection in the option set even after the user keeps
  // typing (which replaces the search results) — otherwise the controlled
  // `value` falls out of `options` and MUI warns / visually resets it.
  const mergedOptions = useMemo(() => {
    if (
      selectedItem &&
      !options.some(
        (o) =>
          o.entityId === selectedItem.entityId &&
          o.entityType === selectedItem.entityType,
      )
    ) {
      return [selectedItem, ...options]
    }
    return options
  }, [options, selectedItem])

  const trimmed = debounced.trim()
  const loading = searchStatus === 'pending'
  const showError = !!(getIn(touched, 'entityId') && getIn(errors, 'entityId'))

  const noOptionsText =
    trimmed.length < MIN_CHARS
      ? 'Type at least 3 characters'
      : 'No leads or opportunities found'

  const handleChange = (_: unknown, item: SearchListItem | null) => {
    setSelectedItem(item)
    // One atomic write — two separate setFieldValue calls each validate against
    // the stale pre-write snapshot, so the half-written state would trip the
    // exactly-one-set rule in useValidations and show an error mid-selection.
    setValues((v) => ({
      ...v,
      entityId: item ? item.entityId : null,
      entityType: item ? mapEntityType(item.entityType) : null,
    }))
    setFieldTouched('entityId', true, false)
    const entityType = item ? mapEntityType(item.entityType) : null
    onRecordChange?.(
      item && entityType
        ? { salesTeamId: item.salesTeamId ?? null, entityType }
        : null,
    )
  }

  return (
    <Autocomplete<SearchListItem>
      options={mergedOptions}
      value={selectedItem}
      disabled={disabled}
      loading={loading}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, value) =>
        option.entityId === value.entityId &&
        option.entityType === value.entityType
      }
      // Results are already filtered server-side — do not re-filter by label.
      filterOptions={(opts) => opts}
      // A Lead and its converted Opportunity can share a company name, so show
      // the type explicitly rather than relying on the (possibly identical) label.
      renderOption={(optionProps, option) => {
        const typeLabel =
          option.entityType === 'Opportunity' ? 'Opportunity' : 'Lead'
        const primary =
          option.companyName ||
          option.name ||
          option.phone ||
          option.email ||
          `${typeLabel} #${option.entityId}`
        const secondary = [option.name, option.phone, option.email]
          .filter((v) => v && v !== primary)
          .join(' · ')
        return (
          <li {...optionProps} key={`${option.entityType}-${option.entityId}`}>
            <Stack sx={{ width: '100%' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Typography variant="body2" noWrap>
                  {primary}
                </Typography>
                <Chip
                  label={typeLabel}
                  size="small"
                  variant="outlined"
                  color={
                    option.entityType === 'Opportunity' ? 'primary' : 'default'
                  }
                />
              </Stack>
              {secondary && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {secondary}
                </Typography>
              )}
            </Stack>
          </li>
        )
      }}
      onInputChange={(_, value, reason) => {
        // Selecting an option fires onInputChange(reason:'reset') with the
        // option label — ignore it, otherwise we fire a wasted /search for it.
        if (reason !== 'reset') setInput(value)
      }}
      onChange={handleChange}
      onBlur={() => setFieldTouched('entityId', true)}
      noOptionsText={noOptionsText}
      // MUI shows loadingText (not noOptionsText) while loading with no options.
      loadingText="Searching…"
      data-test-id="attach-to-picker"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Attach To (optional)"
          margin="normal"
          size="small"
          error={showError}
          helperText={showError ? getIn(errors, 'entityId') : undefined}
          placeholder="Search leads or opportunities"
          inputProps={{
            ...params.inputProps,
            'data-test-id': 'attach-to-picker-input',
          }}
        />
      )}
    />
  )
}
